import React, { useState, useEffect, useRef } from 'react';
import { useAppContext } from '../contexts/AppContext.tsx';
import { useActualSalesData } from '../hooks/useActualSalesData.tsx';
import { format, addMonths, subMonths } from 'date-fns';
import { Link } from 'react-router-dom';
import { ko } from 'date-fns/locale';

const ActualSalesManagementPage: React.FC = () => {
  const { isLoading: contextLoading, registerActualSalesRefresh } = useAppContext();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const yearMonth = format(currentMonth, 'yyyy-MM');

  const {
    teamActualSales,
    totalActualSales,
    assistDetails,
    isLoading,
    isCalculating,
    dataSource,
    lastCalculated,
    error,
    refreshData
  } = useActualSalesData(yearMonth);

  // 🆕 실제매출 자동화 새로고침 함수를 AppContext에 등록
  // useRef를 사용하여 최신 refreshData/register 참조 보장 (mount 1회 등록)
  const refreshDataRef = useRef<((targetMonth?: string) => Promise<void>) | null>(null);
  refreshDataRef.current = refreshData;

  const registerRef = useRef(registerActualSalesRefresh);
  registerRef.current = registerActualSalesRefresh;

  useEffect(() => {
    // 업로드된 월(targetMonth) 우선 새로고침 — 화면 표시 월이 아닌 업로드 월을 재계산·저장
    const refreshForUpload = async (targetMonth?: string) => {
      await refreshDataRef.current?.(targetMonth);
    };
    registerRef.current(refreshForUpload);
    console.log('[ActualSalesManagementPage] 실제매출 새로고침 함수 등록됨');

    return () => {
      // unmount 시 등록 해제 — null 등록 시 AppContext의 자동 등록 effect(!refreshActualSales 조건)가
      // 기본 새로고침 함수를 복원하므로 언마운트 후 stale closure 잔존을 방지한다
      // (registerActualSalesRefresh 계약상 해제 API가 없어 null 등록으로 해제)
      registerRef.current(null as unknown as (targetMonth?: string) => Promise<void>);
      refreshDataRef.current = null;
      console.log('[ActualSalesManagementPage] 실제매출 새로고침 함수 등록 해제됨');
    };
    // register/refreshData는 ref로 최신 참조 보장 — mount/unmount 시에만 실행
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePreviousMonth = () => {
    setCurrentMonth(prev => subMonths(prev, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(prev => addMonths(prev, 1));
  };

  const handleRefresh = async () => {
    console.log('[ActualSalesManagementPage] 수동 새로고침 실행');
    await refreshData();
  };

  // 데이터 소스별 아이콘 및 색상
  const getDataSourceInfo = () => {
    switch (dataSource) {
      case 'cached':
        return { icon: '💾', text: '저장된 데이터', color: 'text-green-600', bgColor: 'bg-green-50' };
      case 'teamCollection_based':
        return { icon: '🏆', text: 'teamCollection 기반 계산', color: 'text-blue-600', bgColor: 'bg-blue-50' };
      default:
        return { icon: '⚠️', text: '데이터 없음', color: 'text-gray-500', bgColor: 'bg-gray-50' };
    }
  };

  const dataSourceInfo = getDataSourceInfo();

  if (contextLoading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">시스템 초기화 중...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* 헤더 */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">💰 실제매출 자동화 관리 (teamCollection 기반)</h1>
          </div>
          
          {/* 데이터 소스 표시 */}
          <div className={`px-4 py-2 rounded-lg ${dataSourceInfo.bgColor} flex items-center gap-2`}>
            <span className="text-lg">{dataSourceInfo.icon}</span>
            <span className={`font-medium ${dataSourceInfo.color}`}>
              {dataSourceInfo.text}
            </span>
          </div>
        </div>
      </div>

      {/* 월 선택 및 컨트롤 */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* 월 네비게이션 */}
          <div className="flex items-center gap-4">
            <button
              onClick={handlePreviousMonth}
              className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
              aria-label="이전 월"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <h2 className="text-xl font-semibold text-gray-900">
              {format(currentMonth, 'yyyy년 MM월', { locale: ko })}
            </h2>
            
            <button
              onClick={handleNextMonth}
              className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
              aria-label="다음 월"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* 새로고침 버튼 */}
          <button
            onClick={handleRefresh}
            disabled={isCalculating}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              isCalculating 
                ? 'bg-gray-100 text-gray-500 cursor-not-allowed' 
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            <svg 
              className={`w-4 h-4 ${isCalculating ? 'animate-spin' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {isCalculating ? '계산 중...' : '새로고침'}
          </button>
        </div>
        
        {/* 마지막 계산 시간 */}
        {lastCalculated && (
          <div className="mt-4 text-sm text-gray-500">
            마지막 계산: {new Date(lastCalculated).toLocaleString('ko-KR')}
          </div>
        )}
      </div>

      {/* 에러 표시 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2">
            <span className="text-red-500">⚠️</span>
            <span className="text-red-700 font-medium">오류 발생</span>
          </div>
          <p className="text-red-600 mt-1">{error}</p>
        </div>
      )}

      {/* 로딩 상태 */}
      {isLoading && (
        <div className="bg-white rounded-lg shadow-sm border p-8 mb-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mr-3"></div>
            <span className="text-gray-600">teamCollection 기반 실제매출 데이터 로딩 중...</span>
          </div>
        </div>
      )}

      {/* 미지정 안내 — 0원 초과면 항상 (차단 아님). 끝까지 팀을 못 정한 수납건은 데일리 리포트에서 지정한다 */}
      {!isLoading && (() => {
        const unassignedEntries = Object.entries(assistDetails).filter(([, d]) => d.teamName === '미지정');
        const unassignedTotal = unassignedEntries.reduce((s, [, d]) => s + d.receipts, 0);
        if (unassignedTotal <= 0) return null;
        return (
          <div className="mb-6 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-900" role="status">
            <div className="font-semibold">팀을 정하지 못한 수납이 {unassignedTotal.toLocaleString()}원 있습니다.</div>
            <div className="mt-1 text-orange-800">
              어시스트가 비어 있고, 그날·그 달에 그 진료의사의 어시스트 있는 진료가 없어 자동 배분할 근거가 없는 건입니다.
              이 금액은 어느 팀 실적에도 들어가지 않습니다.{' '}
              <Link to="/daily-report" className="underline font-medium">데일리 리포트</Link>에서 해당 날짜를 열어 팀을 지정하거나, 엑셀의 어시스트 칸을 채워 다시 올리세요.
            </div>
          </div>
        );
      })()}

      {/* 요약 통계 */}
      {!isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">전체 실제매출</p>
                <p className="text-2xl font-bold text-gray-900">
                  {totalActualSales.toLocaleString()}원
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <span className="text-xl">💰</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">팀 수</p>
                <p className="text-2xl font-bold text-gray-900">
                  {Object.keys(teamActualSales).filter(teamName => teamName !== '미지정').length}개
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <span className="text-xl">👥</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">어시스트 수</p>
                <p className="text-2xl font-bold text-gray-900">
                  {(() => {
                    // 미지정 제외하고 실제 어시스트만 추출
                    const realAssists = Object.entries(assistDetails).filter(([, details]) => details.teamName !== '미지정');
                    // 어시스트명에서 팀명 부분 제거하여 중복 제거 (예: "최희정(1팀)" -> "최희정")
                    const uniqueAssistNames = new Set(
                      realAssists.map(([assistKey]) => {
                        // assistKey 형태: "최희정(1팀)" -> "최희정" 추출
                        const match = assistKey.match(/^(.+?)\(/);
                        return match ? match[1] : assistKey;
                      })
                    );
                    return uniqueAssistNames.size;
                  })()}명
                </p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <span className="text-xl">👤</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">평균 팀 매출</p>
                <p className="text-2xl font-bold text-gray-900">
                  {(() => {
                    const realTeams = Object.entries(teamActualSales).filter(([teamName]) => teamName !== '미지정');
                    const realTeamCount = realTeams.length;
                    const realTeamTotalSales = realTeams.reduce((sum, [, sales]) => sum + sales, 0);
                    return realTeamCount > 0 
                      ? Math.round(realTeamTotalSales / realTeamCount).toLocaleString()
                      : '0';
                  })()}원
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <span className="text-xl">📊</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 팀별 실제매출 테이블 */}
      {!isLoading && (
        <div className="bg-white rounded-lg shadow-sm border mb-6">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">팀별 실제매출</h3>
            <p className="text-gray-600 text-sm mt-1">
              Firebase teamCollection의 monthlyProfit(월 총수익) 데이터를 기반으로 자동 계산된 팀별 실제매출입니다
            </p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    팀명
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    수납액
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    지출액
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    순수익
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    비율
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Object.entries(teamActualSales)
                  .sort(([, a], [, b]) => b - a)
                  .map(([teamName, actualSales]) => {
                    const percentage = totalActualSales > 0 ? (actualSales / totalActualSales) * 100 : 0;

                    // 팀별 수납액/지출액 계산 (assistDetails에서 집계)
                    const teamReceipts = Object.values(assistDetails)
                      .filter(d => d.teamName === teamName)
                      .reduce((sum, d) => sum + d.receipts, 0);

                    const teamExpenses = Object.values(assistDetails)
                      .filter(d => d.teamName === teamName)
                      .reduce((sum, d) => sum + d.expenses, 0);
                    // 출처 분리: 어시스트 이름으로 바로 귀속된 금액 / 빈 어시스트 자동 배분 / 관리자 수동 지정
                    const bySource = Object.values(assistDetails)
                      .filter(d => d.teamName === teamName)
                      .reduce((acc, d) => {
                        const k = d.attribution || 'direct';
                        acc[k] = (acc[k] || 0) + d.receipts;
                        return acc;
                      }, {} as Record<string, number>);
                    const hasSplit = (bySource.auto || 0) !== 0 || (bySource.manual || 0) !== 0;

                    return (
                      <tr key={teamName} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="text-sm font-medium text-gray-900">
                              {teamName}
                            </div>
                          </div>
                          {hasSplit && teamName !== '미지정' && (
                            <div className="text-xs text-gray-600 mt-1 tabular-nums">
                              어시스트 있음 {(bySource.direct || 0).toLocaleString()}원
                              {(bySource.auto || 0) !== 0 && <> + 자동 배분 {(bySource.auto || 0).toLocaleString()}원</>}
                              {(bySource.manual || 0) !== 0 && <> + 수동 지정 {(bySource.manual || 0).toLocaleString()}원</>}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="text-sm text-blue-600">
                            {teamReceipts.toLocaleString()}원
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="text-sm text-red-600">
                            {teamExpenses.toLocaleString()}원
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className={`text-sm font-medium ${
                            actualSales >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {actualSales.toLocaleString()}원
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="text-sm text-gray-500">
                            {percentage.toFixed(1)}%
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
              <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-bold text-gray-900">합계</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="text-sm font-bold text-blue-600">
                      {Object.values(assistDetails)
                        .reduce((sum, d) => sum + d.receipts, 0)
                        .toLocaleString()}원
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="text-sm font-bold text-red-600">
                      {Object.values(assistDetails)
                        .reduce((sum, d) => sum + d.expenses, 0)
                        .toLocaleString()}원
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className={`text-sm font-bold ${
                      totalActualSales >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {totalActualSales.toLocaleString()}원
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="text-sm font-bold text-gray-500">100%</div>
                  </td>
                </tr>
              </tfoot>
            </table>

            {Object.keys(teamActualSales).length === 0 && (
              <div className="text-center py-12">
                <div className="text-gray-500">
                  <span className="text-4xl mb-4 block">📊</span>
                  <p className="text-lg font-medium">실제매출 데이터가 없습니다</p>
                  <p className="text-sm mt-1">
                    해당 월의 데일리 리포트 데이터를 확인해주세요
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 어시스트별 상세 데이터 */}
      {!isLoading && Object.keys(assistDetails).length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">어시스트별 상세 데이터</h3>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    어시스트명
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    소속팀
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    수납액
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    지출액
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    순수익
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Object.entries(assistDetails)
                  .sort(([, a], [, b]) => b.profit - a.profit)
                  .map(([assistName, details]) => (
                    <tr key={assistName} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {assistName}
                          {details.attribution === 'auto' && (
                            <span className="ml-2 text-xs font-medium text-orange-800 bg-orange-100 rounded-full px-2 py-0.5">자동 배분</span>
                          )}
                          {details.attribution === 'manual' && (
                            <span className="ml-2 text-xs font-medium text-blue-800 bg-blue-100 rounded-full px-2 py-0.5">수동 지정</span>
                          )}
                          {details.attribution === 'unassigned' && (
                            <span className="ml-2 text-xs font-medium text-red-800 bg-red-100 rounded-full px-2 py-0.5">미지정</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {details.teamName}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm text-blue-600">
                          {details.receipts.toLocaleString()}원
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm text-red-600">
                          {details.expenses.toLocaleString()}원
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className={`text-sm font-medium ${
                          details.profit >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {details.profit.toLocaleString()}원
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
              <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-bold text-gray-900">합계</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {Object.keys(assistDetails).length}명
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="text-sm font-bold text-blue-600">
                      {Object.values(assistDetails)
                        .reduce((sum, d) => sum + d.receipts, 0)
                        .toLocaleString()}원
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="text-sm font-bold text-red-600">
                      {Object.values(assistDetails)
                        .reduce((sum, d) => sum + d.expenses, 0)
                        .toLocaleString()}원
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className={`text-sm font-bold ${
                      Object.values(assistDetails).reduce((sum, d) => sum + d.profit, 0) >= 0
                        ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {Object.values(assistDetails)
                        .reduce((sum, d) => sum + d.profit, 0)
                        .toLocaleString()}원
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

    </div>
  );
};

export default ActualSalesManagementPage; 